import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { useLibraWorkflows } from 'features/libra/ticket/libraTicketsApi'
import uniq from 'lodash/uniq'
import type { FC } from 'react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { AssetStatus, getAssetStatus } from './getAssetStatus'

interface StatusBreakdown {
  [AssetStatus.NotStarted]: number
  [AssetStatus.InProgress]: number
  [AssetStatus.Completed]: number
}

type AssetStatusTrackerProps = {
  tickets: ReadonlyArray<LibraTicket>
  selectedStatus: AssetStatus | undefined
  onStatusSelect: (status: AssetStatus | undefined) => void
}

export const STATUS_CONFIG = {
  [AssetStatus.NotStarted]: {
    label: 'Not Started',
    color: '#d14343',
  },
  [AssetStatus.InProgress]: {
    label: 'In Progress',
    color: '#d89614',
  },
  [AssetStatus.Completed]: {
    label: 'Completed',
    color: '#49a03c',
  },
} as const

export const AssetStatusTracker: FC<AssetStatusTrackerProps> = ({
  tickets,
  selectedStatus,
  onStatusSelect,
}) => {
  const { statusBreakdown, totalAssets } = useStatusBreakdown(tickets)

  const handleSegmentClick = (status: AssetStatus) => {
    onStatusSelect(selectedStatus === status ? undefined : status)
  }

  return (
    <Container>
      <TrackerBar>
        {[
          AssetStatus.NotStarted,
          AssetStatus.InProgress,
          AssetStatus.Completed,
        ].map((status) => {
          const count = statusBreakdown[status]
          const percentage = totalAssets > 0 ? (count / totalAssets) * 100 : 0

          if (count === 0) return null

          const isSelected = selectedStatus === status
          const hasSelection = selectedStatus !== undefined

          return (
            <Segment
              key={status}
              $width={percentage}
              $color={STATUS_CONFIG[status].color}
              $isSelected={isSelected}
              $isDimmed={hasSelection && !isSelected}
              onClick={() => handleSegmentClick(status)}
            />
          )
        })}
      </TrackerBar>
      <LabelsRow>
        {[
          AssetStatus.NotStarted,
          AssetStatus.InProgress,
          AssetStatus.Completed,
        ].map((status) => {
          const count = statusBreakdown[status]
          const isSelected = selectedStatus === status
          const hasSelection = selectedStatus !== undefined

          return (
            <Label
              key={status}
              $color={STATUS_CONFIG[status].color}
              $isSelected={isSelected}
              $isDimmed={hasSelection && !isSelected}
              onClick={() => handleSegmentClick(status)}>
              {STATUS_CONFIG[status].label}: {count}
            </Label>
          )
        })}
      </LabelsRow>
    </Container>
  )
}

/**
 * Calculates the status breakdown for assets based on their tickets
 * @param tickets - All tickets to analyze
 * @returns Status breakdown with counts per status and total assets
 */
const useStatusBreakdown = (
  tickets: ReadonlyArray<LibraTicket>
): { statusBreakdown: StatusBreakdown; totalAssets: number } => {
  // Load workflows to check ticket progress
  const workflowIds = useMemo(
    () =>
      uniq(
        tickets
          .map((ticket) => ticket.workflowId)
          .filter((id): id is string => id !== undefined)
      ),
    [tickets]
  )

  const { libraWorkflows } = useLibraWorkflows(
    useMemo(() => ({ ids: workflowIds }), [workflowIds])
  )

  const workflowMap = useMemo(
    () => new Map(libraWorkflows.map((w) => [w.id, w])),
    [libraWorkflows]
  )

  return useMemo(() => {
    // Get unique unit IDs from tickets
    const unitIds = uniq(
      tickets
        .map((ticket) => ticket.tags.unit)
        .filter((unit): unit is string => unit !== undefined)
    )

    const breakdown = unitIds.reduce<StatusBreakdown>(
      (acc, unitId) => {
        const unitTickets = tickets.filter(
          (ticket) => ticket.tags.unit === unitId
        )

        const status = getAssetStatus(unitTickets, workflowMap)
        acc[status]++

        return acc
      },
      {
        'not-started': 0,
        'in-progress': 0,
        completed: 0,
      }
    )

    return {
      statusBreakdown: breakdown,
      totalAssets: unitIds.length,
    }
  }, [tickets, workflowMap])
}

const Container = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.gray[800]};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const TrackerBar = styled.div`
  height: 1.75rem;
  width: 100%;
  display: flex;
  gap: 0.125rem;
  background: ${({ theme }) => theme.colors.gray[700]};
  border-radius: 6px;
  overflow: hidden;
  padding: 0.125rem;
`

const Segment = styled.div<{
  $width: number
  $color: string
  $isSelected: boolean
  $isDimmed: boolean
}>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ $color }) => $color};
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: ${({ $isDimmed }) => ($isDimmed ? 0.3 : 1)};
  transform: ${({ $isSelected }) =>
    $isSelected ? 'scaleY(1.1)' : 'scaleY(1)'};

  &:hover {
    opacity: ${({ $isDimmed }) => ($isDimmed ? 0.5 : 0.9)};
  }

  &:first-child {
    border-radius: 4px 0 0 4px;
  }

  &:last-child {
    border-radius: 0 4px 4px 0;
  }

  &:only-child {
    border-radius: 4px;
  }
`

const LabelsRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`

const Label = styled.div<{
  $color: string
  $isSelected: boolean
  $isDimmed: boolean
}>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme, $color, $isSelected, $isDimmed }) =>
    $isDimmed
      ? theme.forecast.secondaryText
      : $isSelected
        ? $color
        : theme.forecast.activeText};
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: ${({ $isDimmed }) => ($isDimmed ? 0.5 : 1)};

  &:hover {
    opacity: ${({ $isDimmed }) => ($isDimmed ? 0.7 : 0.8)};
  }

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 2px;
    background: ${({ $color }) => $color};
    opacity: ${({ $isDimmed }) => ($isDimmed ? 0.3 : 1)};
  }
`
